import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { reportUrl, reportsUrl } from './botReports';

// What the API says when a request fails, else the error itself.
const messageOf = (ex, fallback) =>
  ex?.response?.data?.error?.message || ex?.response?.data?.message || ex?.toString?.() || fallback;

// The list of reports for the chosen filters (newest first, without the markdown).
export function useBotReports(filters) {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const requestId = useRef(0);

  const { account, botId, from, to } = filters;
  const [state, setState] = useState({ loading: false, error: null, items: [] });

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiRef.current.request('GET', reportsUrl({ account, botId, from, to }));
      if (id !== requestId.current) return;
      setState({ loading: false, error: null, items: Array.isArray(response.data) ? response.data : [] });
    } catch (ex) {
      if (id !== requestId.current) return;
      console.error('Failed to load the bot reports:', ex);
      setState({ loading: false, error: messageOf(ex, 'Failed to load the bot reports'), items: [] });
    }
  }, [account, botId, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
}

// One whole report, including its markdown.
export function useBotReport(reportId) {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const requestId = useRef(0);

  const [state, setState] = useState({ loading: false, error: null, report: null });

  const load = useCallback(async () => {
    if (!reportId) return;
    const id = ++requestId.current;
    setState({ loading: true, error: null, report: null });
    try {
      const response = await apiRef.current.request('GET', reportUrl(reportId));
      if (id !== requestId.current) return;
      setState({ loading: false, error: null, report: response.data || null });
    } catch (ex) {
      if (id !== requestId.current) return;
      console.error('Failed to load the bot report:', ex);
      setState({ loading: false, error: messageOf(ex, 'Failed to load the bot report'), report: null });
    }
  }, [reportId]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
}
